import {
  Component,
  OnDestroy,
  signal,
  ChangeDetectionStrategy,
  inject,
  computed,
  effect,
  untracked,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';

import { TabsModule } from 'primeng/tabs';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { TableRowReorderEvent } from 'primeng/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  TfDataTableComponent,
  TfColumnDefDirective,
  ColumnDef,
  TfPageEvent,
  ActionClickEvent,
  EmptyStateComponent,
  SettingsListHeader,
  SettingsLoadingPlaceholder,
} from '@tourfold/libraries/shared-ui';
import { TagModule } from 'primeng/tag';
import { SortEvent } from 'primeng/api';
import { finalize, firstValueFrom, Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TwilioConfigDialog } from './shared/sms-dialog/twilio-config-dialog/twilio-config-dialog';
import { CustomConfigDialog } from './shared/sms-dialog/custom-sms-config-dialog/custom-sms-config-dialog';
import { TestSmsDialog, TestLogEntry } from './shared/sms-dialog/test-sms-dialog/test-sms-dialog';
import {
  CustomSmsParameterDto,
  CustomSmsSettings,
  SmsGateway as SmsGatewayDto,
  SmsGatewayTypeEnum,
  CreateOrUpdateSmsGateway,
  CreateOrUpdateSmsGatewayTypeEnum,
  TwilioGatewayConfig,
  SmsReport,
} from '@tourfold/libraries/tf-api-client-public-v2';
import {
  readTabFromQueryParams,
  syncTabToUrl,
  readDateParam,
  readStringParam,
  encodeDateParam,
  syncQueryParams,
  nonEmptyOrUndefined,
} from '@tourfold/libraries/shared-util';
import { SmsGatewayService } from './shared/sms-gateway.service';
import { FrontendConfigurationService } from '@tourfold/shared-kernels/frontend-configuration';
import { SseV2Service } from '@tourfold/shared-kernels/realtime';
import { SmsGatewayTestResultEventDto } from '@tourfold/shared-kernels/realtime';
import {
  SmsReportFilters,
  SmsReportFilterValues,
} from './shared/sms-report-filters/sms-report-filters';
import { ResourceAccessService } from '@tourfold/shared-kernels/resource-access';
import { provideSmsGatewayAiPageContext } from './settings-sms.ai-page-context';
import {
  resolveSettingsLoadErrorDetails,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';

const TAB_VALUES = ['configuration', 'report'] as const;

@Component({
  selector: 'tf-settings-page-sms-gateway',
  imports: [
    CommonModule,
    SettingsListHeader,
    TabsModule,
    ButtonModule,
    TooltipModule,
    TagModule,
    TwilioConfigDialog,
    CustomConfigDialog,
    TestSmsDialog,
    SmsReportFilters,
    TranslateModule,
    EmptyStateComponent,
    TfDataTableComponent,
    TfColumnDefDirective,
    SettingsLoadingPlaceholder,
    SettingsInlineMessage,
    SettingsLoadError,
  ],
  providers: [],
  templateUrl: './settings-sms.component.html',
  styleUrl: './settings-sms.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SmsGateway implements OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly smsGatewayService = inject(SmsGatewayService);
  private readonly tenantConfigService = inject(FrontendConfigurationService);
  private readonly translate = inject(TranslateService);
  private readonly sseService = inject(SseV2Service);
  private readonly resourceAccessService = inject(ResourceAccessService);
  private readonly tenantId = computed(() => this.tenantConfigService.tenantId());
  private readonly pendingTestTrackingId = signal<string | null>(null);

  readonly canCreateSmsGateway = computed(() =>
    this.resourceAccessService.canPerform('smsGateways', 'create')
  );
  readonly canUpdateSmsGateway = computed(() =>
    this.resourceAccessService.canPerform('smsGateways', 'update')
  );
  readonly canDeleteSmsGateway = computed(() =>
    this.resourceAccessService.canPerform('smsGateways', 'delete')
  );
  readonly canTestSmsGateway = computed(() =>
    this.resourceAccessService.canPerform('smsGateways', 'test')
  );
  private sseSubscription: Subscription | null = null;
  private bufferedTestEvents: SmsGatewayTestResultEventDto[] = [];

  // Gateway Configuration tab
  readonly isTwilioDialogVisible = signal<boolean>(false);
  readonly isCustomDialogVisible = signal<boolean>(false);
  readonly isTestSmsDialogVisible = signal<boolean>(false);
  readonly isLoading = signal<boolean>(false);
  readonly hasLoadedGatewaysOnce = signal(false);
  readonly gatewayInitialLoadFailed = signal(false);
  readonly gatewayLoadError = signal<string | null>(null);
  readonly gatewayLoadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly gatewayActionError = signal<string | null>(null);
  readonly editingConfiguration = signal<SmsGatewayDto | null>(null);
  readonly testingConfiguration = signal<SmsGatewayDto | null>(null);
  readonly twilioInitialConfig = signal<Partial<TwilioGatewayConfig> | null>(null);
  readonly customInitialConfig = signal<Partial<CustomSmsSettings> | null>(null);
  readonly customInitialParameters = signal<CustomSmsParameterDto[]>([]);
  readonly configurations = signal<SmsGatewayDto[]>([]);
  readonly showConfigurationEmptyState = computed(
    () => this.hasLoadedGatewaysOnce() && this.configurations().length === 0
  );

  // Test SMS log
  readonly testLogEntries = signal<TestLogEntry[]>([]);
  readonly testInProgress = signal<boolean>(false);

  // Gateway Configuration tab columns
  readonly configColumns = computed<ColumnDef[]>(() => [
    { field: 'reorder', title: '', type: 'reorderHandle', width: '2rem' },
    { field: 'name', title: 'SETTINGS_SMS.GATEWAY.NAME', type: 'text' },
    { field: 'priority', title: 'SETTINGS_SMS.GATEWAY.PRIORITY', type: 'custom' },
    { field: 'type', title: 'SETTINGS_SMS.GATEWAY.TYPE', type: 'custom' },
    { field: 'endpoint', title: 'SETTINGS_SMS.GATEWAY.PHONE_URL', type: 'custom' },
    {
      field: 'actions',
      title: 'SETTINGS_SMS.GATEWAY.ACTIONS',
      type: 'actions',
      width: '12rem',
      actions: [
        {
          action: 'test',
          icon: 'pi pi-send',
          tooltip: 'SETTINGS_SMS.GATEWAY.TEST_SMS_TOOLTIP',
          severity: 'secondary',
          visible: (_row: unknown) => this.canTestSmsGateway(),
        },
        {
          action: 'edit',
          icon: 'pi pi-pencil',
          tooltip: 'SETTINGS_SMS.GATEWAY.EDIT_TOOLTIP',
          severity: 'secondary',
          visible: (_row: unknown) => this.canUpdateSmsGateway(),
        },
        {
          action: 'delete',
          icon: 'pi pi-trash',
          tooltip: 'SETTINGS_SMS.GATEWAY.DELETE_TOOLTIP',
          severity: 'secondary',
          visible: (_row: unknown) => this.canDeleteSmsGateway(),
        },
      ],
    },
  ]);

  // Report tab
  readonly reportColumns: ColumnDef[] = [
    {
      field: 'scheduledAt',
      title: 'SETTINGS_SMS.REPORT.SCHEDULED_AT',
      type: 'date',
      dateFormat: 'short',
      sortable: true,
    },
    { field: 'toNumber', title: 'SETTINGS_SMS.REPORT.TO_NUMBER', type: 'text', sortable: true },
    { field: 'fromNumber', title: 'SETTINGS_SMS.REPORT.FROM_NUMBER', type: 'text', sortable: true },
    { field: 'message', title: 'SETTINGS_SMS.REPORT.MESSAGE', type: 'custom' },
    {
      field: 'gatewayName',
      title: 'SETTINGS_SMS.REPORT.GATEWAY_NAME',
      type: 'text',
      sortable: true,
    },
    {
      field: 'sentAt',
      title: 'SETTINGS_SMS.REPORT.SENT_AT',
      type: 'date',
      dateFormat: 'short',
      sortable: true,
    },
    { field: 'status', title: 'SETTINGS_SMS.REPORT.STATUS', type: 'custom', sortable: true },
    { field: 'failureReason', title: 'SETTINGS_SMS.REPORT.FAILURE_REASON', type: 'custom' },
  ];

  readonly activeTab = signal(
    readTabFromQueryParams(this.route.snapshot.queryParams, 'tab', TAB_VALUES, 'configuration')
  );
  readonly smsReports = signal<SmsReport[]>([]);
  readonly isReportLoading = signal<boolean>(false);
  readonly hasLoadedReportsOnce = signal(false);
  readonly reportInitialLoadFailed = signal(false);
  readonly reportLoadError = signal<string | null>(null);
  readonly reportLoadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly reportActionError = signal<string | null>(null);
  readonly totalReportRecords = signal(0);
  readonly reportSortField = signal<string | null>('scheduledAt');
  readonly reportSortOrder = signal<number>(-1);
  readonly reportFilterValues = signal<SmsReportFilterValues>({
    fromDate: readDateParam(this.route.snapshot.queryParams, 'fromDate'),
    toDate: readDateParam(this.route.snapshot.queryParams, 'toDate'),
    status:
      nonEmptyOrUndefined(readStringParam(this.route.snapshot.queryParams, 'status', '')) ?? null,
  });

  private isReportInitializing = true;

  readonly hasActiveReportFilters = computed(() => {
    const filters = this.reportFilterValues();
    return filters.fromDate != null || filters.toDate != null || filters.status != null;
  });

  constructor() {
    this.route.queryParams.pipe(takeUntilDestroyed()).subscribe((params) => {
      this.activeTab.set(readTabFromQueryParams(params, 'tab', TAB_VALUES, 'configuration'));
    });

    effect(() => {
      const currentTenantId = this.tenantId();
      if (currentTenantId) {
        untracked(() => {
          this.loadGateways();
          if (this.activeTab() === 'report') {
            this.loadSmsReport();
          }
        });
      }
    });

    this.sseSubscription = this.sseService.on('SMS_GATEWAY_TEST_RESULT:V1').subscribe((event) => {
      const testEvent = event as unknown as SmsGatewayTestResultEventDto;
      const trackingId = this.pendingTestTrackingId();
      if (trackingId && testEvent.tracking_id === trackingId) {
        this.handleTestResultEvent(testEvent);
      } else if (!trackingId) {
        // SSE event arrived before the HTTP response set the tracking ID — buffer it.
        this.bufferedTestEvents.push(testEvent);
      }
      // Events for a different trackingId are silently dropped (stale/unrelated).
    });

    provideSmsGatewayAiPageContext({
      configurations: this.configurations,
      editingConfiguration: this.editingConfiguration,
      testingConfiguration: this.testingConfiguration,
      activeTab: this.activeTab,
    });
  }

  ngOnDestroy(): void {
    this.sseSubscription?.unsubscribe();
  }

  onTabChange(tab: string): void {
    this.activeTab.set(tab);
    syncTabToUrl(this.router, this.route, 'tab', tab);
    if (tab === 'report') {
      // Reset initializing flag and load report
      this.isReportInitializing = true;
      this.loadSmsReport();
    }
  }

  private loadGateways(): void {
    const hadLoadedOnce = this.hasLoadedGatewaysOnce();
    this.isLoading.set(true);
    this.gatewayActionError.set(null);
    this.smsGatewayService
      .listGateways()
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
        })
      )
      .subscribe({
        next: (gateways) => {
          this.configurations.set(
            [...gateways].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
          );
          this.gatewayLoadError.set(null);
          this.gatewayLoadErrorDetails.set(null);
          this.gatewayInitialLoadFailed.set(false);
          this.hasLoadedGatewaysOnce.set(true);
        },
        error: (error: unknown) => {
          const message = this.translate.instant('SETTINGS.LOAD_ERROR.TITLE');
          if (hadLoadedOnce) {
            this.hasLoadedGatewaysOnce.set(true);
            this.gatewayActionError.set(message);
          } else {
            this.gatewayLoadError.set(message);
            this.gatewayInitialLoadFailed.set(true);
            void this.updateGatewayLoadErrorDetails(error);
          }
        },
      });
  }

  private handleTestResultEvent(event: SmsGatewayTestResultEventDto): void {
    const config = this.testingConfiguration();
    const isCustomGateway = config?.type === SmsGatewayTypeEnum.CustomHttp;

    if (event.status === 'SENT') {
      this.appendLogEntry(this.translate.instant('SETTINGS_SMS.GATEWAY.TEST_LOG.SENT'), 'info');

      if (isCustomGateway) {
        // Custom gateways don't provide delivery confirmation
        this.appendLogEntry(
          this.translate.instant('SETTINGS_SMS.GATEWAY.TEST_LOG.COMPLETE'),
          'info'
        );
        this.pendingTestTrackingId.set(null);
      } else {
        this.appendLogEntry(
          this.translate.instant('SETTINGS_SMS.GATEWAY.TEST_LOG.WAITING_FOR_DELIVERY'),
          'info'
        );
      }
    } else if (event.status === 'DELIVERED' || event.status === 'READ') {
      this.pendingTestTrackingId.set(null);
      this.appendLogEntry(
        this.translate.instant('SETTINGS_SMS.GATEWAY.TEST_LOG.DELIVERED'),
        'info'
      );
      this.appendLogEntry(this.translate.instant('SETTINGS_SMS.GATEWAY.TEST_LOG.COMPLETE'), 'info');
    } else if (event.status === 'FAILED') {
      this.pendingTestTrackingId.set(null);
      const reason =
        event.failure_reason ??
        this.translate.instant('SETTINGS_SMS.GATEWAY.TEST_DELIVERY_FAILED_MESSAGE');
      this.appendLogEntry(
        this.translate.instant('SETTINGS_SMS.GATEWAY.TEST_LOG.FAILED_WITH_REASON', { reason }),
        'error'
      );
    }
  }

  private appendLogEntry(message: string, severity: 'info' | 'success' | 'error'): void {
    this.testLogEntries.update((entries) => [...entries, { message, severity }]);
  }

  reloadGateways(): void {
    if (this.tenantId()) {
      this.loadGateways();
    }
  }

  reloadReport(): void {
    this.loadSmsReport();
  }

  clearReportActionError(): void {
    this.reportActionError.set(null);
  }

  openTwilioConfiguration(): void {
    this.isTwilioDialogVisible.set(true);
  }

  openCustomConfiguration(): void {
    this.isCustomDialogVisible.set(true);
  }

  onTwilioDialogVisibleChange(visible: boolean): void {
    this.isTwilioDialogVisible.set(visible);
    if (!visible) {
      this.editingConfiguration.set(null);
      this.twilioInitialConfig.set(null);
    }
  }

  onCustomDialogVisibleChange(visible: boolean): void {
    this.isCustomDialogVisible.set(visible);
    if (!visible) {
      this.editingConfiguration.set(null);
      this.customInitialConfig.set(null);
      this.customInitialParameters.set([]);
    }
  }

  readonly handleTwilioSave = async (payload: {
    name: string;
    enabled: boolean;
    config: TwilioGatewayConfig;
  }): Promise<void> => {
    if (!this.tenantId()) {
      return;
    }

    const editing = this.editingConfiguration();
    const request: CreateOrUpdateSmsGateway = {
      name: payload.name,
      type: CreateOrUpdateSmsGatewayTypeEnum.Twilio,
      enabled: payload.enabled,
      priority: editing?.priority ?? this.configurations().length,
      twilioConfig: payload.config,
    };

    this.isLoading.set(true);
    try {
      await firstValueFrom(
        editing
          ? this.smsGatewayService.replaceGateway(editing.id, request)
          : this.smsGatewayService.createGateway(request)
      );
      this.loadGateways();
      this.isTwilioDialogVisible.set(false);
      this.editingConfiguration.set(null);
      this.twilioInitialConfig.set(null);
    } catch (error: unknown) {
      this.gatewayActionError.set(this.translate.instant('SETTINGS.ACTION_ERROR.SAVE_FAILED'));
      throw error;
    } finally {
      this.isLoading.set(false);
    }
  };

  readonly handleCustomSave = async (payload: {
    name: string;
    enabled: boolean;
    config: CustomSmsSettings;
  }): Promise<void> => {
    if (!this.tenantId()) {
      return;
    }

    const editing = this.editingConfiguration();
    const request: CreateOrUpdateSmsGateway = {
      name: payload.name,
      type: CreateOrUpdateSmsGatewayTypeEnum.CustomHttp,
      enabled: payload.enabled,
      priority: editing?.priority ?? this.configurations().length,
      customConfig: payload.config,
    };

    this.isLoading.set(true);
    try {
      await firstValueFrom(
        editing
          ? this.smsGatewayService.replaceGateway(editing.id, request)
          : this.smsGatewayService.createGateway(request)
      );
      this.loadGateways();
      this.isCustomDialogVisible.set(false);
      this.editingConfiguration.set(null);
      this.customInitialConfig.set(null);
      this.customInitialParameters.set([]);
    } catch (error: unknown) {
      this.gatewayActionError.set(this.translate.instant('SETTINGS.ACTION_ERROR.SAVE_FAILED'));
      throw error;
    } finally {
      this.isLoading.set(false);
    }
  };

  editConfiguration(config: SmsGatewayDto): void {
    this.editingConfiguration.set(config);
    if (config.type === SmsGatewayTypeEnum.Twilio && config.twilioConfig) {
      this.twilioInitialConfig.set(config.twilioConfig);
      this.openTwilioConfiguration();
      return;
    }

    if (config.type === SmsGatewayTypeEnum.CustomHttp && config.customConfig) {
      this.customInitialConfig.set(config.customConfig);
      this.customInitialParameters.set(config.customConfig.parameters ?? []);
      this.openCustomConfiguration();
    }
  }

  deleteConfiguration(config: SmsGatewayDto): void {
    if (!this.tenantId()) {
      return;
    }
    this.isLoading.set(true);
    this.smsGatewayService
      .deleteGateway(config.id)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: () => this.loadGateways(),
        error: () =>
          this.gatewayActionError.set(this.translate.instant('SETTINGS.ACTION_ERROR.DELETE_FAILED')),
      });
  }

  clearGatewayActionError(): void {
    this.gatewayActionError.set(null);
  }

  private async updateGatewayLoadErrorDetails(error: unknown): Promise<void> {
    this.gatewayLoadErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
  }

  private async updateReportLoadErrorDetails(error: unknown): Promise<void> {
    this.reportLoadErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
  }

  canDelete(): boolean {
    return true;
  }

  getStatusLabel(config: SmsGatewayDto): string {
    const active = this.configurations().filter((c) => c.enabled);
    const index = active.findIndex((c) => c.id === config.id);
    if (index === 0) {
      return this.translate.instant('SETTINGS_SMS.GATEWAY.PRIMARY');
    }
    if (index > 0) {
      return this.translate.instant('SETTINGS_SMS.GATEWAY.FALLBACK', { index });
    }
    return this.translate.instant('SETTINGS_SMS.GATEWAY.DISABLED');
  }

  isPrimary(config: SmsGatewayDto): boolean {
    const active = this.configurations().filter((c) => c.enabled);
    const index = active.findIndex((c) => c.id === config.id);
    return index === 0;
  }

  getStatusSeverity(config: SmsGatewayDto): 'success' | 'warn' | 'danger' {
    if (!config.enabled) {
      return 'danger';
    }
    const active = this.configurations().filter((c) => c.enabled);
    const index = active.findIndex((c) => c.id === config.id);
    return index === 0 ? 'success' : 'warn';
  }

  getTypeLabel(type: SmsGatewayTypeEnum): string {
    if (type === SmsGatewayTypeEnum.Twilio) {
      return 'Twilio';
    }
    if (type === SmsGatewayTypeEnum.CustomHttp) {
      return 'Custom HTTP';
    }
    return type;
  }

  getEndpoint(config: SmsGatewayDto): string {
    if (config.type === SmsGatewayTypeEnum.Twilio) {
      return config.twilioConfig?.phoneNumber ?? '—';
    }
    return config.customConfig?.url ?? '—';
  }

  getAccountOrMethod(config: SmsGatewayDto): string {
    if (config.type === SmsGatewayTypeEnum.Twilio) {
      return config.twilioConfig?.accountSid ?? '—';
    }
    return config.customConfig?.httpMethod ?? '—';
  }

  getMessagingOrContent(config: SmsGatewayDto): string {
    if (config.type === SmsGatewayTypeEnum.Twilio) {
      return config.twilioConfig?.messagingServiceSid ?? '—';
    }
    return config.customConfig?.contentType ?? '—';
  }

  onToggleEnabled(config: SmsGatewayDto, enabled: boolean): void {
    if (!this.tenantId()) {
      return;
    }

    this.configurations.set(
      this.configurations().map((c) => (c.id === config.id ? { ...c, enabled } : c))
    );

    this.smsGatewayService
      .toggleGateway(config.id, enabled)
      .pipe(finalize(() => this.loadGateways()))
      .subscribe();
  }

  onRowReorder(event: TableRowReorderEvent): void {
    if (!this.tenantId()) {
      return;
    }

    const reordered =
      ((event as unknown as { value?: SmsGatewayDto[] }).value &&
      Array.isArray((event as unknown as { value?: SmsGatewayDto[] }).value)
        ? (event as unknown as { value?: SmsGatewayDto[] }).value
        : this.configurations()) ?? this.configurations();

    const reorderedWithPriority = reordered.map((cfg, index) => ({
      ...cfg,
      priority: index,
    }));
    this.configurations.set(reorderedWithPriority);

    this.isLoading.set(true);
    this.smsGatewayService
      .reorderGateways(reorderedWithPriority.map((c) => c.id))
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe(() => this.loadGateways());
  }

  onConfigAction(event: ActionClickEvent<SmsGatewayDto>): void {
    switch (event.action) {
      case 'test':
        this.testConfiguration(event.row);
        break;
      case 'edit':
        this.editConfiguration(event.row);
        break;
      case 'delete':
        this.deleteConfiguration(event.row);
        break;
    }
  }

  testConfiguration(config: SmsGatewayDto): void {
    this.testingConfiguration.set(config);
    this.isTestSmsDialogVisible.set(true);
  }

  onTestSmsDialogVisibleChange(visible: boolean): void {
    this.isTestSmsDialogVisible.set(visible);
    if (!visible) {
      this.testingConfiguration.set(null);
      this.testLogEntries.set([]);
      this.testInProgress.set(false);
      this.pendingTestTrackingId.set(null);
      this.bufferedTestEvents = [];
    }
  }

  onTestSmsSend(phoneNumber: string): void {
    const config = this.testingConfiguration();

    if (!this.tenantId() || !config) {
      return;
    }

    this.testInProgress.set(true);
    this.testLogEntries.set([
      {
        message: this.translate.instant('SETTINGS_SMS.GATEWAY.TEST_LOG.SCHEDULING'),
        severity: 'info',
      },
    ]);

    this.smsGatewayService.testGateway(config.id, phoneNumber).subscribe({
      next: (result) => {
        this.pendingTestTrackingId.set(result.trackingId);
        this.appendLogEntry(
          this.translate.instant('SETTINGS_SMS.GATEWAY.TEST_LOG.SCHEDULED'),
          'info'
        );
        this.appendLogEntry(
          this.translate.instant('SETTINGS_SMS.GATEWAY.TEST_LOG.WAITING_FOR_STATUS'),
          'info'
        );

        // Flush any SSE events that arrived before the HTTP response
        const matching = this.bufferedTestEvents.filter((e) => e.tracking_id === result.trackingId);
        this.bufferedTestEvents = [];
        matching.forEach((e) => this.handleTestResultEvent(e));
      },
      error: (err) => {
        this.bufferedTestEvents = [];
        this.pendingTestTrackingId.set(null);

        if (err?.response) {
          err.response
            .json()
            .then((body: any) => {
              const detail =
                body?.detail ?? this.translate.instant('SETTINGS_SMS.GATEWAY.TEST_ERROR_MESSAGE');
              this.appendLogEntry(
                this.translate.instant('SETTINGS_SMS.GATEWAY.TEST_LOG.FAILED_WITH_REASON', {
                  reason: detail,
                }),
                'error'
              );
            })
            .catch(() => {
              this.appendLogEntry(
                this.translate.instant('SETTINGS_SMS.GATEWAY.TEST_LOG.FAILED_WITH_REASON', {
                  reason: this.translate.instant('SETTINGS_SMS.GATEWAY.TEST_ERROR_MESSAGE'),
                }),
                'error'
              );
            });
        } else {
          this.appendLogEntry(
            this.translate.instant('SETTINGS_SMS.GATEWAY.TEST_LOG.FAILED_WITH_REASON', {
              reason: this.translate.instant('SETTINGS_SMS.GATEWAY.TEST_ERROR_MESSAGE'),
            }),
            'error'
          );
        }
      },
    });
  }

  // Report methods
  private loadSmsReport(page = 0, size = 50): void {
    if (!untracked(() => this.tenantId())) {
      this.isReportLoading.set(false);
      return;
    }

    const hadLoadedOnce = this.hasLoadedReportsOnce();
    this.isReportLoading.set(true);
    this.reportActionError.set(null);

    let fromDateObj: Date | undefined;
    let toDateObj: Date | undefined;

    const filters = untracked(() => this.reportFilterValues());

    // Convert dates to UTC with proper start/end times (like audit log)
    if (filters.fromDate) {
      const year = filters.fromDate.getFullYear();
      const month = filters.fromDate.getMonth();
      const day = filters.fromDate.getDate();
      fromDateObj = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
    }

    if (filters.toDate) {
      const year = filters.toDate.getFullYear();
      const month = filters.toDate.getMonth();
      const day = filters.toDate.getDate();
      toDateObj = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
    }

    const sortArray: string[] = [];
    const field = this.reportSortField();
    const order = this.reportSortOrder();

    if (field && order !== 0) {
      const backendField = this.mapReportFieldToBackend(field);
      const direction = order === 1 ? 'asc' : 'desc';
      sortArray.push(`${backendField}:${direction}`);
    } else {
      // Default sort
      sortArray.push('scheduled_at:desc');
    }

    this.smsGatewayService
      .querySmsReport({
        from: fromDateObj,
        to: toDateObj,
        status: filters.status as any,
        page,
        size,
        sort: sortArray.length > 0 ? sortArray : undefined,
      })
      .pipe(finalize(() => this.isReportLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.smsReports.set(response.items);
          this.totalReportRecords.set(response.page.totalElements);
          this.isReportLoading.set(false);
          this.reportLoadError.set(null);
          this.reportLoadErrorDetails.set(null);
          this.reportInitialLoadFailed.set(false);
          this.hasLoadedReportsOnce.set(true);

          // Mark initialization as complete after first data load
          if (this.isReportInitializing) {
            this.isReportInitializing = false;
          }
        },
        error: (error: unknown) => {
          this.isReportLoading.set(false);
          const message = this.translate.instant('SETTINGS.LOAD_ERROR.TITLE');
          if (hadLoadedOnce) {
            this.hasLoadedReportsOnce.set(true);
            this.reportActionError.set(message);
          } else {
            this.reportLoadError.set(message);
            this.reportInitialLoadFailed.set(true);
            void this.updateReportLoadErrorDetails(error);
          }
          if (this.isReportInitializing) {
            this.isReportInitializing = false;
          }
        },
      });
  }

  onReportFilterApplied(filters: SmsReportFilterValues): void {
    this.reportFilterValues.set(filters);
    this.syncReportFiltersToUrl(filters);
    this.loadSmsReport();
  }

  onReportFilterCleared(): void {
    const cleared: SmsReportFilterValues = {
      fromDate: null,
      toDate: null,
      status: null,
    };
    this.reportFilterValues.set(cleared);
    this.syncReportFiltersToUrl(cleared);
    this.loadSmsReport();
  }

  private syncReportFiltersToUrl(filters: SmsReportFilterValues): void {
    syncQueryParams(this.router, this.route, {
      fromDate: encodeDateParam(filters.fromDate),
      toDate: encodeDateParam(filters.toDate),
      status: filters.status ?? undefined,
    });
  }

  onReportSort(event: SortEvent): void {
    // Ignore sort events during initialization
    if (this.isReportInitializing) {
      return;
    }

    // Check if sort actually changed to prevent infinite loops
    const newField = event.field ?? null;
    const newOrder = event.order ?? 0;

    if (this.reportSortField() === newField && this.reportSortOrder() === newOrder) {
      return; // Sort hasn't changed, don't reload
    }

    this.reportSortField.set(newField);
    this.reportSortOrder.set(newOrder);

    // Reload with new sort
    this.loadSmsReport(0, 50);
  }

  onReportPageChange(event: TfPageEvent): void {
    const page = Math.floor(event.first / event.rows);
    this.loadSmsReport(page, event.rows);
  }

  private mapReportFieldToBackend(field: string): string {
    const fieldMap: Record<string, string> = {
      scheduledAt: 'scheduled_at',
      toNumber: 'to_number',
      fromNumber: 'from_number',
      gatewayName: 'gateway_name',
      sentAt: 'sent_at',
      status: 'status',
    };
    return fieldMap[field] ?? field;
  }

  getStatusBadgeSeverity(status: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' {
    switch (status) {
      case 'DELIVERED':
        return 'success';
      case 'SENT':
        return 'info';
      case 'SENDING':
        return 'secondary';
      case 'SCHEDULED':
        return 'warn';
      case 'FAILED':
        return 'danger';
      default:
        return 'secondary';
    }
  }
}
